/**
 * Map reducer v2 — pure state transitions for the satellite/map UI.
 *
 * Spec: docs/superpowers/specs/2026-05-07-map-state-machine.md
 *   §3 actions, §4 state machine, §13 founder lock-ins
 * Plan: docs/superpowers/plans/2026-05-07-sprint-2026-21-map-refactor.md Task 2
 *
 * Founder architectural lock-ins (do not relax without ADR):
 *   #1 TOGGLE_RASTER_MODE preserves selectedDate across product switches.
 *      User picks 20.04 NDVI, switches to NDVI Контр. → date stays 20.04.
 *      Toggling the same mode off also keeps the date so re-toggle restores
 *      the prior selection.
 *   #2 CLOSE_SAT_PANEL is mute-all for satellite overlays but PRESERVES
 *      cropFillOn — the culture fill is a separate axis from the sat panel.
 *
 * Other invariants:
 *   - Reducer is pure: same input → same output, no side effects, no Date.now.
 *     Callers (effect hooks) inject fetchedAt explicitly via action payload.
 *   - LRU eviction on coverage cache: bounded at COVERAGE_LRU_MAX entries,
 *     evicted by ascending fetchedAt (oldest goes first). Updating an
 *     existing key does NOT trigger eviction — it's an update, not insert.
 *   - Coverage / availableDates Maps are replaced (new instance) on every
 *     write so equality-based selectors fire correctly.
 *   - SET_ACTIVE_SCOPE clears selectedDate + selectedFieldId (need fresh
 *     /dates fetch and the popover would point at a stale field), but KEEPS
 *     activeRasterMode so the user sees the same product in the new scope.
 */

import type {
  MapState,
  MapAction,
  CoverageKey,
  CoverageData,
  PersistentMapState,
} from './mapState';

// Bound: coverage cache LRU max. Tuned for typical session — 4-6 products ×
// 3-4 dates × 1-2 scopes ≈ 16-32. 20 keeps memory <1MB even at largest farms.
const COVERAGE_LRU_MAX = 20;

export const INITIAL_MAP_STATE: MapState = {
  // Persistent prefs
  basemap: 'hybrid',
  // AK-297: color-by-crop ON by default. Прежде поля рендерились голыми
  // brand-контурами без заливки → «каша» (design review 2026-06-27). Заливка
  // по культуре (fill_color из MVT, opacity 0.45) — primary способ читать карту
  // полей; пользователь по-прежнему может выключить тогглом «Заливка по культуре».
  // Mutual-exclusion с raster-продуктами сохранён (predicate cropFillOn &&
  // activeRasterMode === 'none'): при включении NDVI заливка скрывается.
  cropFillOn: true,
  // AK-211: слой «Рельеф» выключен по умолчанию — включается тумблером
  // «Рельеф» в RightRail. При включении гасит crop-fill и raster (visibility
  // predicates), показывая choropleth по уклону.
  terrainOn: false,
  labels: { number: true, crop: true, icon: false, area: false },
  contourMode: 'planting',
  legendPosition: { x: 64, y: 176 },  // founder lock-in #5: draggable px coords
  satelliteStripPosition: null,        // AK-319: null = default bottom-center anchor

  // Satellite session vars
  activeRasterMode: 'none',
  activeProduct: null,
  selectedDate: null,
  rgbSceneOn: false,
  cloudMaskOn: false,
  satelliteStripOpen: false,

  // Scope & selection
  activeScope: { type: 'company', id: null },
  selectedFieldId: null,
  focusedCropId: null,
  scopeDrawerOpen: false,

  // Coverage / dates caches
  coverage: new Map(),
  availableDates: new Map(),
  availableDatesStatus: new Map(),
  coverageStatus: { kind: 'idle' },
};

/**
 * Evict oldest entries (by fetchedAt asc) until size ≤ COVERAGE_LRU_MAX.
 * Returns a new Map; never mutates input. Called only after a fresh insert
 * — updates of existing keys keep size constant and skip this path.
 */
function evictLRU(coverage: Map<CoverageKey, CoverageData>): Map<CoverageKey, CoverageData> {
  if (coverage.size <= COVERAGE_LRU_MAX) return coverage;
  const sorted = Array.from(coverage.entries()).sort(
    (a, b) => a[1].fetchedAt - b[1].fetchedAt,
  );
  // Drop oldest (size - MAX) entries; keep the freshest MAX.
  return new Map(sorted.slice(coverage.size - COVERAGE_LRU_MAX));
}

export function mapReducer(state: MapState, action: MapAction): MapState {
  switch (action.type) {
    // ─── Persistent prefs ────────────────────────────────────────────────────
    case 'SET_BASEMAP':
      return { ...state, basemap: action.basemap };

    case 'TOGGLE_CROP_FILL':
      // Independent axis from raster — see founder lock-in #2 reasoning.
      return { ...state, cropFillOn: !state.cropFillOn };

    case 'TOGGLE_TERRAIN':
      // AK-211: слой «Рельеф» — независимая ось от satellite (не трогает
      // activeRasterMode/date/coverage). Взаимное исключение с crop-fill и
      // raster обеспечивается visibility-предикатами в registry, а не здесь —
      // reducer лишь флипает флаг (как TOGGLE_CROP_FILL).
      return { ...state, terrainOn: !state.terrainOn };

    case 'TOGGLE_LABEL':
      return {
        ...state,
        labels: { ...state.labels, [action.key]: !state.labels[action.key] },
      };

    case 'SET_CONTOUR_MODE':
      return { ...state, contourMode: action.mode };

    case 'SET_LEGEND_POSITION':
      return { ...state, legendPosition: { x: action.x, y: action.y } };

    case 'SET_SATELLITE_STRIP_POSITION':
      return { ...state, satelliteStripPosition: { x: action.x, y: action.y } };

    // ─── Satellite panel (§3, lock-in #1, #2) ────────────────────────────────
    case 'TOGGLE_RASTER_MODE': {
      // Founder lock-in #1: selectedDate is intentionally NOT cleared here.
      //   - Same mode again → toggle off (return to 'none'), keep date for
      //     possible re-toggle.
      //   - Different mode → switch product, keep date so user doesn't lose
      //     the timestamp they were inspecting.
      // The action payload type excludes 'none' (compile-time guarantee).
      const next = state.activeRasterMode === action.mode ? 'none' : action.mode;
      return { ...state, activeRasterMode: next };
    }

    case 'OPEN_SAT_PANEL':
      // Explicit open — sets product + (optional) date and opens the strip.
      // Distinct from TOGGLE_SATELLITE_STRIP which only flips visibility.
      return {
        ...state,
        satelliteStripOpen: true,
        activeProduct: action.product,
        selectedDate: action.date ?? state.selectedDate,
      };

    case 'CLOSE_SAT_PANEL':
      // Founder lock-in #2: mute-all for sat overlays, but cropFillOn
      // (culture fill) is PRESERVED — different UX axis. Also clears
      // activeProduct so a subsequent open starts clean.
      return {
        ...state,
        activeRasterMode: 'none',
        activeProduct: null,
        rgbSceneOn: false,
        cloudMaskOn: false,
        selectedDate: null,
        satelliteStripOpen: false,
        // cropFillOn UNTOUCHED — founder lock-in #2
      };

    case 'SET_SELECTED_DATE':
      return { ...state, selectedDate: action.date };

    case 'TOGGLE_RGB_SCENE':
      return { ...state, rgbSceneOn: !state.rgbSceneOn };

    case 'TOGGLE_CLOUD_MASK':
      return { ...state, cloudMaskOn: !state.cloudMaskOn };

    case 'TOGGLE_SATELLITE_STRIP':
      return { ...state, satelliteStripOpen: !state.satelliteStripOpen };

    case 'SET_SATELLITE_STRIP':
      return { ...state, satelliteStripOpen: action.open };

    // ─── Scope & selection ───────────────────────────────────────────────────
    case 'SET_ACTIVE_SCOPE': {
      // Clears scope-specific session: stale selectedDate (re-fetch /dates),
      // stale selectedFieldId (popover would target a field that may not
      // exist in the new scope).
      //
      // AK-106 (Sprint #2 2026-05-08, founder lock-in): satellite products
      // are FARM-ONLY. When switching to a non-farm scope (company/cluster/
      // holding/brigade/array/field/tenant), reset all satellite session
      // vars (mute-all, mirrors CLOSE_SAT_PANEL semantics — lock-in #2).
      // For farm→farm transitions the user keeps activeRasterMode so the
      // same product loads automatically once /dates resolves.
      const isFarm = action.scope.type === 'farm';
      if (!isFarm) {
        return {
          ...state,
          activeScope: action.scope,
          selectedDate: null,
          selectedFieldId: null,
          // Satellite mute-all (only on switch AWAY from farm)
          activeRasterMode: 'none',
          activeProduct: null,
          rgbSceneOn: false,
          cloudMaskOn: false,
          satelliteStripOpen: false,
        };
      }
      return {
        ...state,
        activeScope: action.scope,
        selectedDate: null,
        selectedFieldId: null,
      };
    }

    case 'SELECT_FIELD':
      return { ...state, selectedFieldId: action.fieldId };

    case 'CLEAR_SELECTED_FIELD':
      return { ...state, selectedFieldId: null };

    case 'SET_FOCUSED_CROP':
      return { ...state, focusedCropId: action.cropId };

    case 'TOGGLE_SCOPE_DRAWER':
      return { ...state, scopeDrawerOpen: !state.scopeDrawerOpen };

    case 'SET_SCOPE_DRAWER':
      return { ...state, scopeDrawerOpen: action.open };

    // ─── Coverage fetch lifecycle ────────────────────────────────────────────
    case 'COVERAGE_FETCH_START':
      return { ...state, coverageStatus: { kind: 'loading' } };

    case 'COVERAGE_FETCH_SUCCESS': {
      // Insert-or-update: existing key → just replace (no eviction); new key
      // → insert and apply LRU bound. evictLRU is a no-op when ≤ MAX.
      const next = new Map(state.coverage);
      next.set(action.key, action.data);
      return {
        ...state,
        coverage: evictLRU(next),
        coverageStatus: { kind: 'idle' },
      };
    }

    case 'COVERAGE_FETCH_ERROR':
      return {
        ...state,
        coverageStatus: { kind: 'error', message: action.message },
      };

    // ─── Available-dates fetch lifecycle ─────────────────────────────────────
    case 'DATES_FETCH_SUCCESS': {
      const nextDates = new Map(state.availableDates);
      nextDates.set(action.key, action.dates);
      // AK-604-follow: status only ever accompanies an empty dates Set, but
      // always clear any stale status on a fresh success so a key that goes
      // empty → has-data → empty again doesn't show a stale reason.
      const nextStatus = new Map(state.availableDatesStatus);
      if (action.status) {
        nextStatus.set(action.key, action.status);
      } else {
        nextStatus.delete(action.key);
      }
      return { ...state, availableDates: nextDates, availableDatesStatus: nextStatus };
    }

    // ─── Hydration (§3.4) ────────────────────────────────────────────────────
    case 'HYDRATE': {
      // Restore ONLY persistent fields. Session state (raster, dates,
      // coverage, selection) is intentionally not rehydrated — fresh on
      // every page load.
      const p = action.prefs as Partial<PersistentMapState>;
      return {
        ...state,
        basemap:        p.basemap        ?? state.basemap,
        cropFillOn:     p.cropFillOn     ?? state.cropFillOn,
        terrainOn:      p.terrainOn      ?? state.terrainOn,
        labels:         { ...state.labels, ...(p.labels ?? {}) },
        contourMode:    p.contourMode    ?? state.contourMode,
        legendPosition: p.legendPosition ?? state.legendPosition,
        satelliteStripPosition:
          p.satelliteStripPosition ?? state.satelliteStripPosition,
      };
    }

    default:
      return state;
  }
}
